const STORAGE_KEY = 'peoplevue_user_roles';
const SERVER_ROLE_KEY = 'peoplevue_server_role';
const API_BASE = import.meta.env.VITE_API_BASE_URL;

// Set by syncRole so a later setUserRole (the admin's own dropdown change)
// can push straight through without the caller having to thread a token.
let apiToken = null;

function loadRoles() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveRoles(roles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(roles));
  } catch {
    // ignore
  }
}

function getServerRole() {
  try {
    const stored = localStorage.getItem(SERVER_ROLE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Fetch the signed-in user's server-derived role (GET /me: the ADMIN_OIDS
 * allowlist merged with UI-assigned roles from the roles blob) and cache it
 * locally. Admins also hydrate the full role map so the People & Access
 * table shows everyone's real role. Call once after sign-in. Non-fatal.
 */
export async function syncRole(token) {
  if (!API_BASE || !token) return null;
  apiToken = token;
  try {
    const res = await fetch(`${API_BASE}/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Me API ${res.status}`);
    const me = await res.json();
    localStorage.setItem(SERVER_ROLE_KEY, JSON.stringify(me));

    // Admin? Pull the tenant-wide role map (endpoint 403s for everyone else).
    if (me.isAdmin) {
      try {
        const rolesRes = await fetch(`${API_BASE}/roles`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });
        if (rolesRes.ok) {
          const { roles } = await rolesRes.json();
          if (roles && typeof roles === 'object') saveRoles(roles);
        }
      } catch (err) {
        console.warn('Failed to sync role map (non-fatal):', err.message);
      }
    }
    return me;
  } catch (err) {
    console.warn('Failed to sync role (non-fatal):', err.message);
    return null;
  }
}

// First person to open the app on this device becomes admin in the LOCAL
// roles map. This is the fallback that keeps admin UI reachable until the
// /me endpoint is deployed — once the server has answered (see syncRole),
// getUserRole prefers server truth and this local flag stops mattering.
// Server endpoints enforce ADMIN_OIDS on every write regardless.
export function bootstrapAdmin(userId) {
  if (!userId) return;
  // Server role already known? Don't self-grant anything.
  if (getServerRole()) return;
  const existing = loadRoles();
  if (!existing) {
    saveRoles({ [userId]: 'admin' });
  }
}

function initRoles(employeeIds) {
  const existing = loadRoles();
  if (existing) return existing;
  const roles = {};
  employeeIds.forEach((id) => { roles[id] = 'user'; });
  saveRoles(roles);
  return roles;
}

export function getUserRole(employeeId) {
  if (API_BASE) {
    const server = getServerRole();
    // /me is authoritative for the SIGNED-IN user only. Other people's roles
    // come from the roles map below (hydrated from GET /roles for admins) —
    // the old behavior of answering 'user' for everyone but self is what made
    // the People & Access table lie after a page reload.
    if (server && server.oid === employeeId) return server.isAdmin ? 'admin' : 'user';
  }
  const roles = loadRoles();
  if (!roles) return 'user';
  return roles[employeeId] || 'user';
}

export function isAdmin(employeeId) {
  return getUserRole(employeeId) === 'admin';
}

/**
 * Assign a role from People & Access. Local-first (the table updates
 * immediately), then pushed to the server so it reaches the promoted
 * person's device via their next /me sync. The push is admin-gated and
 * self-change-blocked server-side.
 */
export function setUserRole(employeeId, role) {
  const roles = loadRoles() || {};
  roles[employeeId] = role;
  saveRoles(roles);

  if (API_BASE && apiToken) {
    fetch(`${API_BASE}/roles/${employeeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiToken}` },
      body: JSON.stringify({ role }),
    })
      .then((res) => { if (!res.ok) console.warn(`Role push failed: ${res.status}`); })
      .catch((err) => console.warn('Role push failed:', err.message));
  }
}

export function getAllRoles(employees) {
  const roles = initRoles(employees.map((e) => e.id));
  return employees.map((e) => ({ ...e, role: roles[e.id] || 'user' }));
}
